import { useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router";

import { PROTECTED_ROUTES, seatRecordDestination, type CourseWorkspaceShell } from "@/navigation/routes";

/**
 * The entry for every seat route a retired address can arrive at.
 *
 * Only the ARRIVAL is resolved: the destination is read once per mount and
 * acted on only while the location is still the arrival. A page's own
 * `setSearchParams` is never re-decided. Re-deciding on every render is how the
 * retired Assignments entry caught a page's own record selection and ejected
 * the reader from it. Matching the arrival's location key also lets an arrival
 * resolve within its own route (`?open=` on the course workspace) and then
 * render that route's page.
 *
 * `children` is the page for an arrival that is itself a destination: the
 * course workspace, the learner's academic record, the learner's attendance
 * history. A route with no page of its own lands on the Home.
 */
export function SeatRecordEntry({ shell, children }: { shell: CourseWorkspaceShell; children?: ReactNode }) {
  const location = useLocation();
  const [arrival] = useState(() => ({
    key: location.key,
    destination: seatRecordDestination(shell, location.pathname, location.search, location.hash),
  }));
  if (arrival.destination && location.key === arrival.key) {
    return <Navigate to={arrival.destination} replace />;
  }
  return children ?? <Navigate to={PROTECTED_ROUTES[shell].dashboard} replace />;
}
